import React, { useCallback, useEffect, useState } from "react";

import {
  eventDispatcher,
  eventListener,
  fetchGeolocation,
  IAccount,
  Multicast,
  TOPICS,
} from "@origin-digital/event-dispatcher";
import styled from "styled-components";
import { type SwipeableProps, useSwipeable } from "react-swipeable";
import { isNative } from "@origin-digital/platform-helpers";
import { useUserContext } from "@origin-digital/user-context-provider";
import { BannerView } from "./BannerView";
import {
  bannerState,
  createBannerState,
  CrisisBannerContent,
  loadContent,
  processDisplayLogic,
  updateLocalStorage,
} from "./helpers";

const Container = styled.div`
  position: relative;
`;

export type PageManifestCrisisBanner = "float" | "top" | "none";

const Wrapper = styled.div<{ position: PageManifestCrisisBanner }>`
  position: ${(props) => (props.position === "float" ? "absolute" : "flex")};
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  background-color: white;
`;

export const CrisisBanner = ({
  position = "top",
}: {
  position: PageManifestCrisisBanner;
}) => {
  const [showBanner, setShowBanner] = useState(true);
  const [bannerContent, setBannerContent] = useState<
    CrisisBannerContent[] | undefined
  >(undefined);

  const onCloseButtonClick = () => {
    eventDispatcher.dispatch({
      topic: TOPICS.CRISIS_BANNER_CLOSE,
      payload: {},
      multicast: Multicast.ALL_TABS,
    });
    //Only close displayed banners, in (officially unsupported) case that there are open banners that aren't displayed
    const bannerStatus = createBannerState("closed", bannerContent);
    updateLocalStorage(bannerState, bannerStatus);
    setShowBanner(false);
  };

  const [x, setX] = useState(0);

  const nativeSwipeConfig: SwipeableProps = {
    onSwiped: (eventData) => {
      if (eventData.absX > 200) {
        onCloseButtonClick();
      }
    },
    trackTouch: true,
    trackMouse: true,
    swipeDuration: 1000,
    delta: 5,
    onSwiping: (eventData) => {
      setX(eventData.deltaX);
    },
    onTouchEndOrOnMouseUp: () => {
      setX(0);
    },
  };
  const swipeableOptions = isNative()
    ? nativeSwipeConfig
    : {
        trackTouch: false,
        trackMouse: false,
      };
  const swipeableHandlers = useSwipeable(swipeableOptions);

  useEffect(() => {
    return eventListener.addMulticastListener(
      TOPICS.CRISIS_BANNER_CLOSE,
      () => {
        setShowBanner(false);
      }
    );
  }, [setShowBanner]);

  const loadBanner = useCallback(
    async (accounts?: IAccount[]) => {
      const [rawContent, userLocation] = await Promise.all([
        loadContent(),
        fetchGeolocation({}),
      ]);

      const bannersToDisplay = processDisplayLogic(
        rawContent,
        userLocation,
        position,
        accounts
      );

      if (bannersToDisplay.length === 0) return;

      setShowBanner(true);
      setBannerContent(bannersToDisplay);
    },
    [position]
  );

  const { user } = useUserContext();
  useEffect(() => {
    try {
      loadBanner(user?.accounts);
    } catch (error: unknown) {
      console.error("CrisisBanner Error:", {
        message: (error as Error)?.message || "Unknown Error",
      });
    }
  }, [loadBanner, user?.accounts]);

  return showBanner && bannerContent ? (
    <Container
      data-id="crisis-banner-container"
      style={{ translate: `${x}px 0px` }}
      {...swipeableHandlers}
    >
      <Wrapper position={position}>
        {bannerContent.map((item, index) => {
          return (
            <BannerView
              key={index}
              index={index}
              bannerId={item.id}
              type={item.acf.crisisBannerType}
              heading={item.title.rendered}
              message={item.acf.crisisMessage}
              onCloseButtonClick={onCloseButtonClick}
            />
          );
        })}
      </Wrapper>
    </Container>
  ) : null;
};
